import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  DeleteOutline as DeleteOutlineIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format, parseISO, isValid } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import libroDiarioService, {
  LibroDiarioDetalle,
  LibroDiarioCreateDto,
  DeLiDiLineaWriteDto
} from '../../services/libroDiarioService';
import imputableService from '../../services/imputableService';
import type { Imputable } from '../../services/imputableService';

function newLineKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
}

interface LineDraft {
  key: string;
  nroCuenta: number | '';
  nomCuentaDisplay: string;
  detalle: string;
  debe: string;
  haber: string;
}

function emptyLines(count: number): LineDraft[] {
  return Array.from({ length: count }, () => ({
    key: newLineKey(),
    nroCuenta: '',
    nomCuentaDisplay: '',
    detalle: '',
    debe: '',
    haber: ''
  }));
}

function parseNum(s: string): number {
  const t = s.trim().replace(',', '.');
  if (!t) return 0;
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

function totals(lines: LineDraft[]): { debe: number; haber: number } {
  return lines.reduce(
    (acc, l) => ({
      debe: acc.debe + parseNum(l.debe),
      haber: acc.haber + parseNum(l.haber)
    }),
    { debe: 0, haber: 0 }
  );
}

function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

const maxDetalle = 35;

const CuentaAutocomplete: React.FC<{
  disabled: boolean;
  nroCuenta: number | '';
  nomDisplay: string;
  onPick: (nro: number, nom: string) => void;
}> = ({ disabled, nroCuenta, nomDisplay, onPick }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<Imputable[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const q = input.trim() || (nroCuenta !== '' ? String(nroCuenta) : '');
        const res = await imputableService.getImputablesSearchPaged({
          pageNumber: 1,
          pageSize: 20,
          searchTerm: q
        });
        const raw = res.items as unknown as Record<string, unknown>[];
        setOptions(
          raw.map((item) => ({
            nroCuenta: Number(item.nroCuenta ?? item.NroCuenta ?? 0),
            nomCuenta: String(item.nomCuenta ?? item.NomCuenta ?? '')
          }))
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [input, nroCuenta]);

  const value: Imputable | null = useMemo(() => {
    if (nroCuenta === '' || nroCuenta == null) return null;
    return {
      nroCuenta: Number(nroCuenta),
      nomCuenta: nomDisplay || String(nroCuenta)
    };
  }, [nroCuenta, nomDisplay]);

  return (
    <Autocomplete
      disabled={disabled}
      loading={loading}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (o?.nroCuenta != null ? `${o.nroCuenta} — ${o.nomCuenta ?? ''}` : '')}
      isOptionEqualToValue={(a, b) => a?.nroCuenta === b?.nroCuenta}
      value={value}
      onChange={(_, v) => {
        if (v?.nroCuenta != null) onPick(v.nroCuenta, v.nomCuenta ?? '');
      }}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      renderInput={(params) => <TextField {...params} size="small" label="Cuenta" placeholder="Buscar…" />}
      sx={{ minWidth: 208, maxWidth: '100%' }}
    />
  );
};

const AsientoDiarioForm: React.FC = () => {
  const navigate = useNavigate();
  const { nroAsiento: paramRaw } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);

  const isNew = paramRaw === 'nuevo';
  const nroParsed = paramRaw && paramRaw !== 'nuevo' ? parseInt(paramRaw, 10) : NaN;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<LibroDiarioDetalle | null>(null);

  const [fechaAsiento, setFechaAsiento] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [aclaracion, setAclaracion] = useState('');
  const [lineas, setLineas] = useState<LineDraft[]>(() => emptyLines(2));

  const [confirmContab, setConfirmContab] = useState(false);
  const [confirmDesc, setConfirmDesc] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  /** Fecha del asiento tal como quedó tras la última carga/guardado (para habilitar «Guardar fecha» SUPERADMIN). */
  const [fechaBaseCargada, setFechaBaseCargada] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) setFechaBaseCargada(null);
  }, [isNew]);

  const handleVolver = useCallback(() => {
    // Pestaña nueva u origen sin stack: no hay "atrás" útil → listado de asientos.
    if (typeof window !== 'undefined' && window.history.length <= 1) {
      navigate('/contabilidad/asientos-diario');
      return;
    }
    navigate(-1);
  }, [navigate]);

  const loadDetail = useCallback(async () => {
    if (isNew || !Number.isFinite(nroParsed)) return;
    try {
      setLoading(true);
      const d = await libroDiarioService.getById(nroParsed);
      setDetail(d);
      const fechaNorm = d.fechaAsiento
        ? format(isValid(parseISO(d.fechaAsiento)) ? parseISO(d.fechaAsiento) : new Date(), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd');
      setFechaAsiento(fechaNorm);
      setFechaBaseCargada(fechaNorm);
      setAclaracion((d.aclaracion || '').trim());
      setLineas(
        d.lineas.map((ln) => ({
          key: newLineKey(),
          nroCuenta: ln.nroCuenta ?? '',
          nomCuentaDisplay: ln.nomCuenta?.trim() || '',
          detalle: (ln.detalle || '').trim(),
          debe: ln.debe != null && ln.debe !== 0 ? String(ln.debe) : '',
          haber: ln.haber != null && ln.haber !== 0 ? String(ln.haber) : ''
        }))
      );
    } catch (e) {
      enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
      navigate('/contabilidad/asientos-diario');
    } finally {
      setLoading(false);
    }
  }, [isNew, nroParsed, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const td = totals(lineas);
  const balanceado = Math.abs(td.debe - td.haber) < 0.0001;

  const frozenPorContabilizado = !!detail?.estaContabilizado;
  const vinculadoAComprobante = !!detail && !detail.esManual;
  const cabeceraReadOnly = frozenPorContabilizado || vinculadoAComprobante;
  const fechaAsientoEditable = !cabeceraReadOnly || superAdmin;
  const montosYDetalleReadOnly = frozenPorContabilizado || vinculadoAComprobante;
  const cuentaReadOnly = frozenPorContabilizado;
  const puedeAgregarQuitarLineas = (isNew || !!detail?.esManual) && !frozenPorContabilizado;

  const fechaDirty =
    !isNew && detail != null && fechaBaseCargada != null && fechaAsiento !== fechaBaseCargada;

  const canSave = !frozenPorContabilizado && balanceado && lineas.length >= 2;
  const canSaveFechaSuperAdmin = superAdmin && !isNew && detail != null && fechaDirty;
  const guardarHabilitado = canSave || canSaveFechaSuperAdmin;
  const canDelete = detail?.esManual && !detail.estaContabilizado;
  const canContabilizar = detail && !detail.estaContabilizado;
  const canDescontabilizar = detail?.estaContabilizado && superAdmin;

  const buildPayload = (): LibroDiarioCreateDto | null => {
    const lineasDto: DeLiDiLineaWriteDto[] = [];
    for (const l of lineas) {
      if (l.nroCuenta === '') continue;
      const debe = parseNum(l.debe);
      const haber = parseNum(l.haber);
      lineasDto.push({
        nroCuenta: Number(l.nroCuenta),
        detalle: l.detalle.trim().slice(0, maxDetalle),
        debe,
        haber
      });
    }
    if (lineasDto.length < 2) {
      enqueueSnackbar('Se requieren al menos dos líneas con cuenta.', { variant: 'warning' });
      return null;
    }
    const deb = lineasDto.reduce((s, x) => s + x.debe, 0);
    const hab = lineasDto.reduce((s, x) => s + x.haber, 0);
    if (Math.abs(deb - hab) > 0.0001) {
      enqueueSnackbar('El asiento no cuadra (debe ≠ haber).', { variant: 'warning' });
      return null;
    }
    return {
      fechaAsiento,
      aclaracion: aclaracion.trim(),
      lineas: lineasDto
    };
  };

  const handleSave = async () => {
    if (canSave) {
      const payload = buildPayload();
      if (!payload) return;
      try {
        setSaving(true);
        if (isNew) {
          const created = await libroDiarioService.create(payload);
          enqueueSnackbar('Asiento creado', { variant: 'success' });
          navigate(`/contabilidad/asientos-diario/${created.nroAsiento}`, { replace: true });
        } else if (Number.isFinite(nroParsed)) {
          await libroDiarioService.update(nroParsed, payload);
          enqueueSnackbar('Asiento guardado', { variant: 'success' });
          await loadDetail();
        }
      } catch (e) {
        enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
      } finally {
        setSaving(false);
      }
      return;
    }
    if (canSaveFechaSuperAdmin && Number.isFinite(nroParsed)) {
      try {
        setSaving(true);
        await libroDiarioService.patchFechaAsiento(nroParsed, fechaAsiento);
        enqueueSnackbar('Fecha del asiento actualizada', { variant: 'success' });
        await loadDetail();
      } catch (e) {
        enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
      } finally {
        setSaving(false);
      }
    }
  };

  const handleContabilizar = async () => {
    if (!detail) return;
    try {
      setSaving(true);
      await libroDiarioService.contabilizar(detail.nroAsiento);
      enqueueSnackbar('Asiento contabilizado', { variant: 'success' });
      setConfirmContab(false);
      await loadDetail();
    } catch (e) {
      enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDescontabilizar = async () => {
    if (!detail) return;
    try {
      setSaving(true);
      await libroDiarioService.descontabilizar(detail.nroAsiento);
      enqueueSnackbar('Candado quitado', { variant: 'success' });
      setConfirmDesc(false);
      await loadDetail();
    } catch (e) {
      enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    try {
      setSaving(true);
      await libroDiarioService.delete(detail.nroAsiento);
      enqueueSnackbar('Asiento eliminado', { variant: 'success' });
      setConfirmDel(false);
      navigate('/contabilidad/asientos-diario');
    } catch (e) {
      enqueueSnackbar(libroDiarioService.parseError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLineas((prev) => prev.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  };

  const addLine = () => setLineas((prev) => [...prev, ...emptyLines(1)]);
  const removeLine = (key: string) => {
    setLineas((prev) => (prev.length <= 2 ? prev : prev.filter((x) => x.key !== key)));
  };

  if (loading && !isNew) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const btnSx = { borderRadius: 2, textTransform: 'none' as const, fontWeight: 500 };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleVolver}
              sx={btnSx}
            >
              Volver
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{ flexGrow: 1, fontWeight: 600, color: '#1976d2', minWidth: 0, lineHeight: 1.25 }}
            >
              {isNew
                ? 'Nuevo asiento manual'
                : (() => {
                    const nro = detail?.nroAsiento ?? nroParsed;
                    const comp = detail ? (detail.comprobante ?? '').trim() : '';
                    return comp
                      ? `Asiento ${nro} - Comprobante asociado: ${comp}`
                      : `Asiento ${nro}`;
                  })()}
            </Typography>
            {!isNew && detail?.estaContabilizado && (
              <Tooltip title="Contabilizado">
                <LockIcon color="action" />
              </Tooltip>
            )}
          </Stack>

          {detail && !detail.esManual && !detail.estaContabilizado && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Este asiento está vinculado a un comprobante generado por otro módulo. Solo puede
              cambiar la cuenta imputable en cada línea; fecha, aclaración, detalle e importes no son editables.
              {superAdmin &&
                ' Como SUPERADMIN podés corregir la fecha del asiento y guardar con el botón «Guardar».'}
            </Alert>
          )}
          {detail && !detail.esManual && detail.estaContabilizado && (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
              Asiento vinculado a comprobante y contabilizado: no admite cambios
              {superAdmin
                ? '. Como SUPERADMIN podés corregir solo la fecha y guardar con «Guardar».'
                : '.'}
            </Alert>
          )}
          {detail?.estaContabilizado && detail.esManual && superAdmin && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Asiento contabilizado: la edición está bloqueada salvo la fecha (SUPERADMIN). Modificá la fecha y
              pulsá «Guardar».
            </Alert>
          )}

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            Datos del asiento
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Tooltip
                title={
                  superAdmin && cabeceraReadOnly
                    ? 'SUPERADMIN: podés corregir la fecha incluso con asiento contabilizado o vinculado a comprobante.'
                    : ''
                }
                disableHoverListener={!(superAdmin && cabeceraReadOnly)}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Fecha asiento"
                  type="date"
                  value={fechaAsiento}
                  onChange={(e) => setFechaAsiento(e.target.value)}
                  disabled={!fechaAsientoEditable}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Aclaración"
                value={aclaracion}
                onChange={(e) => setAclaracion(e.target.value.slice(0, 256))}
                disabled={cabeceraReadOnly}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 1 }} flexWrap="wrap" useFlexGap>
            <Button variant="contained" disabled={!guardarHabilitado || saving} onClick={handleSave} sx={{ ...btnSx, px: 3, py: 1.25 }}>
              {saving ? 'Guardando…' : canSaveFechaSuperAdmin && !canSave ? 'Guardar fecha' : 'Guardar'}
            </Button>
            {canContabilizar && (
              <Button variant="outlined" color="secondary" onClick={() => setConfirmContab(true)} sx={btnSx}>
                Contabilizar
              </Button>
            )}
            {canDescontabilizar && (
              <Button variant="outlined" color="warning" onClick={() => setConfirmDesc(true)} sx={btnSx}>
                Descontabilizar (SUPERADMIN)
              </Button>
            )}
            {canDelete && (
              <Button variant="outlined" color="error" onClick={() => setConfirmDel(true)} sx={btnSx}>
                Eliminar
              </Button>
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Stack direction="row" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', flexGrow: 1 }}>
              Líneas del asiento
            </Typography>
            {puedeAgregarQuitarLineas && (
              <Button size="small" startIcon={<AddIcon />} onClick={addLine} sx={btnSx}>
                Agregar línea
              </Button>
            )}
          </Stack>

          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table
              size="small"
              sx={{ tableLayout: 'fixed', width: '100%' }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Cuenta</TableCell>
                  <TableCell sx={{ width: '24%', minWidth: 260, maxWidth: 292 }}>
                    Detalle (máx. {maxDetalle})
                  </TableCell>
                  <TableCell align="right" sx={{ width: 180, minWidth: 180 }}>
                    Debe
                  </TableCell>
                  <TableCell align="right" sx={{ width: 180, minWidth: 180 }}>
                    Haber
                  </TableCell>
                  {puedeAgregarQuitarLineas && <TableCell width={56} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {lineas.map((ln) => (
                  <TableRow key={ln.key}>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <CuentaAutocomplete
                        disabled={cuentaReadOnly}
                        nroCuenta={ln.nroCuenta}
                        nomDisplay={ln.nomCuentaDisplay}
                        onPick={(nro, nom) =>
                          updateLine(ln.key, { nroCuenta: nro, nomCuentaDisplay: nom })
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', width: '24%', minWidth: 260, maxWidth: 292 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={ln.detalle}
                        disabled={montosYDetalleReadOnly}
                        inputProps={{ maxLength: maxDetalle }}
                        helperText={`${ln.detalle.length}/${maxDetalle}`}
                        FormHelperTextProps={{ sx: { mx: 0, mt: 0.5 } }}
                        onChange={(e) => updateLine(ln.key, { detalle: e.target.value })}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ width: 180, minWidth: 180, verticalAlign: 'top' }}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        inputProps={{
                          min: 0,
                          step: '0.01',
                          style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
                        }}
                        value={ln.debe}
                        disabled={montosYDetalleReadOnly}
                        onChange={(e) => updateLine(ln.key, { debe: e.target.value })}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ width: 180, minWidth: 180, verticalAlign: 'top' }}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        inputProps={{
                          min: 0,
                          step: '0.01',
                          style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
                        }}
                        value={ln.haber}
                        disabled={montosYDetalleReadOnly}
                        onChange={(e) => updateLine(ln.key, { haber: e.target.value })}
                      />
                    </TableCell>
                    {puedeAgregarQuitarLineas && (
                      <TableCell>
                        <IconButton size="small" onClick={() => removeLine(ln.key)} disabled={lineas.length <= 2}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={3} sx={{ mt: 2 }} justifyContent="flex-end" flexWrap="wrap">
            <Typography variant="body2">
              Total debe: <strong>{td.debe.toFixed(2)}</strong>
            </Typography>
            <Typography variant="body2">
              Total haber: <strong>{td.haber.toFixed(2)}</strong>
            </Typography>
            <Typography variant="body2" color={balanceado ? 'success.main' : 'error'}>
              Diferencia: <strong>{(td.debe - td.haber).toFixed(2)}</strong>
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmContab} onClose={() => setConfirmContab(false)}>
        <DialogTitle>Contabilizar asiento</DialogTitle>
        <DialogContent>¿Confirma contabilizar? No podrá editarlo hasta que un SUPERADMIN descontabilice.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmContab(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleContabilizar} disabled={saving}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDesc} onClose={() => setConfirmDesc(false)}>
        <DialogTitle>Descontabilizar</DialogTitle>
        <DialogContent>Solo SUPERADMIN. ¿Quitar el candado y permitir edición?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDesc(false)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleDescontabilizar} disabled={saving}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDel} onClose={() => setConfirmDel(false)}>
        <DialogTitle>Eliminar asiento</DialogTitle>
        <DialogContent>¿Eliminar este asiento manual? Esta acción no se puede deshacer.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AsientoDiarioForm;
